"use client";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import type { ToolUIPart } from "ai";
import { CheckCircleIcon, XCircleIcon } from "lucide-react";
import { type ReactNode } from "react";
import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationRejected,
  ConfirmationTitle,
} from "@/components/ai-elements/confirmation";
import { ToolOutput } from "@/components/ai-elements/tool";
import type {
  ChatAddToolApprovalResponse,
  ChatAddToolResult,
  ChatRetryApprovalContinuation,
  ChatSendMessage,
} from "@/lib/ai/chat-message-renderer-contract";
import { getRenderableToolOutput } from "@/lib/ai/message-view/tool-output-presentation";
import { ApproveButton } from "./approve-button";
import { DenyButton } from "./deny-button";
import {
  NativeToolApprovalProvider,
  useNativeToolApproval,
} from "./native-tool-approval";
import {
  ToolApprovalActionLabelProvider,
  ToolApprovalReviewContent,
  useToolApprovalActionLabels,
  useToolApprovalActionState,
} from "./tool-approval-review";

const DEFAULT_APPROVE_LABEL = msg`Approve`;
const DEFAULT_DENY_LABEL = msg`Deny`;

function hasApproval(part: ToolUIPart): part is ToolUIPart & {
  approval: { id: string; approved?: boolean; reason?: string };
} {
  return "approval" in part && Boolean(part.approval);
}

type NativeToolApprovalCardProps = {
  part: ToolUIPart;
  toolName: string;
  addToolApprovalResponse: ChatAddToolApprovalResponse;
  addToolResult: ChatAddToolResult;
  canRetryApprovalContinuation?: boolean;
  approveLabel?: MessageDescriptor;
  approveVariant?: "default" | "destructive";
  denyLabel?: MessageDescriptor;
  resultContent?: ReactNode;
  reviewContent?: ReactNode;
  isRetryingApprovalContinuation?: boolean;
  retryApprovalContinuation?: ChatRetryApprovalContinuation;
  retryableApprovalToolCallId?: string;
  /** 自動承認ポリシーが現在の stream 内で判定中かを区別する。 */
  isStreaming?: boolean;
  sendMessage: ChatSendMessage;
};

/**
 * Tool 固有の確認内容を AI Elements の共通 approval 操作へ接続します。
 */
export function NativeToolApprovalCard({
  approveLabel = DEFAULT_APPROVE_LABEL,
  approveVariant = "default",
  denyLabel = DEFAULT_DENY_LABEL,
  ...props
}: NativeToolApprovalCardProps) {
  return (
    <ToolApprovalActionLabelProvider
      key={props.part.toolCallId}
      approveLabel={approveLabel}
      denyLabel={denyLabel}
      reviewRequired={Boolean(props.reviewContent)}
    >
      <NativeToolApprovalCardContent
        {...props}
        approveVariant={approveVariant}
      />
    </ToolApprovalActionLabelProvider>
  );
}

function NativeToolApprovalCardContent({
  part,
  toolName,
  addToolApprovalResponse,
  addToolResult,
  canRetryApprovalContinuation = false,
  approveVariant = "default",
  resultContent,
  reviewContent,
  isRetryingApprovalContinuation = false,
  retryApprovalContinuation,
  retryableApprovalToolCallId,
  isStreaming = false,
  sendMessage,
}: Omit<NativeToolApprovalCardProps, "approveLabel" | "denyLabel">) {
  const { i18n } = useLingui();
  const { approveLabel, denyLabel } = useToolApprovalActionLabels();
  const approveActionState = useToolApprovalActionState();
  if (!hasApproval(part)) {
    return null;
  }

  const isAutomatic = part.approval.isAutomatic === true;
  // 自動判定は stream と同時に終了する。停止・再読込後も request が残った場合は、
  // 手動 Approve でポリシーを迂回させず、Deny だけを復旧操作として許可する。
  const isInterruptedAutomaticRequest =
    isAutomatic && part.state === "approval-requested" && !isStreaming;

  return (
    <Confirmation
      approval={part.approval}
      className="rounded-none border-0 bg-transparent shadow-none"
      state={part.state}
    >
      {!reviewContent || isAutomatic ? (
        <ConfirmationTitle className="font-medium text-foreground">
          {part.state !== "approval-requested" ? (
            <Trans>This tool request was reviewed.</Trans>
          ) : isAutomatic && !isInterruptedAutomaticRequest ? (
            <Trans>Checking approval for this tool request.</Trans>
          ) : (
            <Trans>Review this tool request before execution.</Trans>
          )}
        </ConfirmationTitle>
      ) : null}

      {reviewContent ? (
        <ToolApprovalReviewContent className="col-start-2">
          {reviewContent}
        </ToolApprovalReviewContent>
      ) : null}

      <ConfirmationAccepted>
        <div className="col-start-2 flex items-center gap-1.5 text-emerald-700 text-xs dark:text-emerald-300">
          <CheckCircleIcon aria-hidden="true" className="size-4" />
          {isAutomatic ? (
            <Trans>This tool execution was approved automatically.</Trans>
          ) : (
            <Trans>You approved this tool execution.</Trans>
          )}
        </div>
      </ConfirmationAccepted>

      <ConfirmationRejected>
        <div className="col-start-2 flex items-center gap-1.5 text-destructive text-xs">
          <XCircleIcon aria-hidden="true" className="size-4" />
          {isAutomatic ? (
            <Trans>This tool execution was denied automatically.</Trans>
          ) : (
            <Trans>You denied this tool execution.</Trans>
          )}
        </div>
      </ConfirmationRejected>

      {part.state === "output-available" ? (
        <div className="col-span-full">
          {resultContent ?? (
            <ToolOutput
              errorText={undefined}
              output={getRenderableToolOutput(part.output)}
            />
          )}
        </div>
      ) : null}

      {part.state === "output-error" ? (
        <div className="col-span-full">
          <ToolOutput errorText={part.errorText} output={undefined} />
        </div>
      ) : null}

      {part.approval.reason ? (
        <p className="col-start-2 text-muted-foreground text-xs">
          {part.approval.reason}
        </p>
      ) : null}

      {(!isAutomatic || isInterruptedAutomaticRequest) &&
      part.state === "approval-requested" ? (
        <NativeToolApprovalProvider
          approvalId={part.approval.id}
          addToolApprovalResponse={addToolApprovalResponse}
        >
          <NativeApprovalResponseControls
            part={part}
            toolName={toolName}
            addToolResult={addToolResult}
            approveLabel={i18n._(approveLabel)}
            approveActionState={
              isInterruptedAutomaticRequest ? "hidden" : approveActionState
            }
            approveVariant={approveVariant}
            denyLabel={i18n._(denyLabel)}
            sendMessage={sendMessage}
          />
        </NativeToolApprovalProvider>
      ) : null}

      {part.state === "approval-responded" &&
      part.toolCallId === retryableApprovalToolCallId &&
      retryApprovalContinuation &&
      (canRetryApprovalContinuation || isRetryingApprovalContinuation) ? (
        <div className="col-start-2 flex pt-0.5">
          <ConfirmationAction
            disabled={
              !canRetryApprovalContinuation || isRetryingApprovalContinuation
            }
            onClick={() =>
              void retryApprovalContinuation().catch(() => undefined)
            }
            variant="outline"
          >
            {isRetryingApprovalContinuation ? (
              <Trans>Retrying continuation...</Trans>
            ) : (
              <Trans>Retry continuation</Trans>
            )}
          </ConfirmationAction>
        </div>
      ) : null}
    </Confirmation>
  );
}

function NativeApprovalResponseControls({
  part,
  toolName,
  addToolResult,
  approveLabel,
  approveActionState,
  approveVariant,
  denyLabel,
  sendMessage,
}: {
  part: ToolUIPart;
  toolName: string;
  addToolResult: ChatAddToolResult;
  approveLabel: string;
  approveActionState: "enabled" | "disabled" | "hidden";
  approveVariant: "default" | "destructive";
  denyLabel: string;
  sendMessage: ChatSendMessage;
}) {
  const nativeApproval = useNativeToolApproval();

  return (
    <>
      {part.state === "approval-requested" &&
      nativeApproval?.responseDisabledReason ? (
        nativeApproval.responseDisabledReason === "error" ? (
          <div className="col-start-2 flex flex-wrap items-center gap-2 text-muted-foreground text-xs leading-5">
            <span id={nativeApproval.responseDisabledReasonId}>
              <Trans>Could not check who can respond to this request.</Trans>
            </span>
            <ConfirmationAction
              onClick={nativeApproval.retryPermissionCheck}
              size="sm"
              variant="outline"
            >
              <Trans>Retry permission check</Trans>
            </ConfirmationAction>
          </div>
        ) : (
          <p
            className="col-start-2 text-muted-foreground text-xs leading-5"
            id={nativeApproval.responseDisabledReasonId}
          >
            {nativeApproval.responseDisabledReason === "forbidden" ? (
              <Trans>
                Only the chat creator can approve or deny this request.
              </Trans>
            ) : nativeApproval.responseDisabledReason === "checking" ? (
              <Trans>Checking who can respond to this request.</Trans>
            ) : (
              <Trans>
                This chat must be saved before this request can be answered.
              </Trans>
            )}
          </p>
        )
      ) : null}

      <ConfirmationActions className="col-start-2 pt-0.5">
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={toolName}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          {denyLabel}
        </DenyButton>
        {approveActionState !== "hidden" ? (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={toolName}
            addToolResult={addToolResult}
            disabled={approveActionState === "disabled"}
            sendMessage={sendMessage}
            variant={approveVariant}
          >
            {approveLabel}
          </ApproveButton>
        ) : null}
      </ConfirmationActions>
    </>
  );
}
